/**
 * Helpers to turn a Formik/Yup error bag into feedback the user can actually see.
 *
 * Why this exists: `submitForm()` resolves *silently* when validation fails. The only
 * feedback is the inline <span> rendered under each field, so an error on a field that
 * is not rendered — or that sits outside the visible area of a scrolling modal — leaves
 * the user clicking a button that appears to do nothing.
 *
 * That is exactly what happened on step 1 of the contractor onboarding wizard: `age` is
 * required by the schema but its input is only rendered for staff, so a contractor whose
 * document had no `age` could never get past "Save and Continue" and never saw a reason.
 */

export type FieldLabelMap = Record<string, string>;

/** "emergencyContactName" -> "Emergency Contact Name" */
const humanizeFieldName = (fieldPath: string): string => {
  const leaf = fieldPath.split(".").pop() || fieldPath;

  return leaf
    .replace(/\[\d+\]/g, "")
    .replace(/[._-]/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (char) => char.toUpperCase());
};

const labelFor = (fieldPath: string, labels: FieldLabelMap): string => {
  // Array items fall back to the label of their parent field, so
  // `preferredAirportIds[0].Id` reads as "Preferred Local Airports".
  const withoutIndexes = fieldPath.replace(/\[\d+\]/g, "");

  return (
    labels[fieldPath] ||
    labels[withoutIndexes] ||
    labels[withoutIndexes.split(".")[0]] ||
    humanizeFieldName(fieldPath)
  );
};

/**
 * Flattens a (possibly nested) Formik error object into "Label: message" lines.
 * Yup nests errors for objects and arrays, so a plain `Object.values()` is not enough.
 */
export const collectFormErrors = (
  errors: unknown,
  labels: FieldLabelMap = {},
  parentPath = "",
): string[] => {
  if (!errors) return [];

  if (typeof errors === "string") {
    if (!parentPath) return [errors];

    const label = labelFor(parentPath, labels);
    // Yup messages usually already start with the field name ("Email is required!"),
    // so avoid printing it twice.
    return [
      errors.toLowerCase().startsWith(label.toLowerCase())
        ? errors
        : `${label}: ${errors}`,
    ];
  }

  if (Array.isArray(errors)) {
    return errors.flatMap((item, index) =>
      collectFormErrors(item, labels, `${parentPath}[${index}]`),
    );
  }

  if (typeof errors === "object") {
    const errorBag = errors as Record<string, unknown>;

    return Object.keys(errorBag).flatMap((key) =>
      collectFormErrors(
        errorBag[key],
        labels,
        parentPath ? `${parentPath}.${key}` : key,
      ),
    );
  }

  return [];
};

/**
 * Builds the message body for the alert dialog. Returns "" when there is nothing to
 * report, so the caller can treat it as "no errors".
 *
 * The dialog renders the message with `white-space: pre-line`
 * (components/Custom/AlertDialog.tsx), so plain "\n" is enough for the bullet list.
 */
export const buildIncompleteFormMessage = ({
  errors,
  labels = {},
  intro = "Please complete or fix the fields below before continuing:",
}: {
  errors: unknown;
  labels?: FieldLabelMap;
  intro?: string;
}): string => {
  const lines = Array.from(new Set(collectFormErrors(errors, labels)));

  if (!lines.length) return "";

  return `${intro}\n\n${lines.map((line) => `•  ${line}`).join("\n")}`;
};
